/**
 * Company: SPHMedia
 * Description: [Landscape video package component for the homepage story packages]
 */

import useStickyInlinePlayer from "@elements/Player/hooks/useStickyInlinePlayer";
import PlaylistVideoPlayer from "@elements/Player/PlaylistVideoPlayer";
import type { BaseLinkProps } from "@elements/Typography/Link";
import OptionalLink from "@elements/Typography/OptionalLink";
import { LONG_FORM_VIDEOS_PLAYLIST_ID } from "@helper/getEnvVariables";
import stickyController from "@helper/stickyController";
import getProcessedBCResponse from "@hooks/transformer/useBrightcoveResponse";
import { Heading } from "@sphtech/sph-web-ui/elements/Heading";
import type {
  BrightcovePlayer,
  LatestVideoResponse,
} from "@typings/Brightcove.d";
import { ReactElement, useCallback, useRef, useState } from "react";

type LandscapeVideoPackageProps = {
  titleLink?: BaseLinkProps;
};
/**
 * LandscapeVideoPackage
 * Description: [Landscape video package component for the homepage story packages]
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function LandscapeVideoPackage({
  titleLink,
}: LandscapeVideoPackageProps): ReactElement {
  const [isMute, setIsMute] = useState(true);
  const [isSticky, setIsSticky] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<LatestVideoResponse | null>(
    null,
  );
  const [isPlaying, setIsPlaying] = useState(false);

  const playerParentRef = useRef<HTMLDivElement | null>(null);
  const playerWrapperRef = useRef<HTMLDivElement | null>(null);

  const player = useRef<BrightcovePlayer | null>(null);

  // Sync mute state with the player
  const syncMute = useCallback(() => {
    setIsMute(player.current?.muted() ?? true);
  }, []);

  // Sync current video state with the player
  const syncCurrentVideo = useCallback(() => {
    const media = player.current?.mediainfo;

    if (!media || !media.id) return;

    setCurrentVideo((prev) =>
      prev?.id === media.id ? prev : getProcessedBCResponse([media])[0],
    );
  }, []);

  // Sync play state with the player
  const syncPlayState = useCallback(() => {
    setIsPlaying(!player.current?.paused?.());
  }, []);

  // Handle player ready event to sync mute state and current video
  const handlePlayerReady = useCallback(
    (playerInstance: BrightcovePlayer) => {
      player.current = playerInstance;

      syncMute();
      playerInstance.on("volumechange", syncMute);

      syncCurrentVideo();
      playerInstance.on("loadstart", syncCurrentVideo);
      playerInstance.on("playing", syncPlayState);
      playerInstance.on("pause", syncPlayState);

      playerInstance.on("dispose", () => {
        playerInstance.off?.("volumechange", syncMute);
        playerInstance.off?.("loadstart", syncCurrentVideo);
        playerInstance.off?.("playing", syncPlayState);
        playerInstance.off?.("pause", syncPlayState);
      });
    },
    [syncMute, syncCurrentVideo, syncPlayState],
  );

  const playerId = "landscape-video-player";
  useStickyInlinePlayer({
    isMute,
    isPlaying,
    observerRef: playerParentRef,
    playerId,
    setIsSticky,
  });

  return (
    <>
      <div className="text-sm font-semibold text-brand-primary leading-[24px] py-4 border-t-2 border-brand-primary">
        <Heading Element="h4" className="font-primary" testId="heading">
          <OptionalLink link={titleLink}>Videos</OptionalLink>
        </Heading>
      </div>

      <div
        ref={playerParentRef}
        className="flex flex-col gap-2.5"
        id="landscape-video-package"
      >
        <div className={isSticky ? "min-h-[360px] xl:min-h-[480px]" : ""}>
          <div
            className={
              isSticky ? "w-[520px] fixed bottom-4 right-4 z-50" : "w-full"
            }
          >
            <div className="relative">
              <PlaylistVideoPlayer
                playlistId={LONG_FORM_VIDEOS_PLAYLIST_ID}
                forwardedRef={playerWrapperRef}
                handlePlayerReady={handlePlayerReady}
                syncMuteAcrossPlayers={false}
              />

              {isSticky && (
                <div className="z-20 absolute top-0 right-0 flex items-center justify-center w-8 h-8 p-1 rounded-full bg-black-500">
                  <button
                    type="button"
                    aria-label="Close picture-in-picture"
                    onClick={() => {
                      setIsSticky(false);
                      stickyController.clear(playerId);
                    }}
                    className="text-text-inverse text-sm outline-none focus:outline-none"
                  >
                    X
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        {currentVideo && (
          <div className="flex flex-col">
            <h3 className="font-semibold font-secondary text-[22px] text-[#1e1e1e] mb-[5px]">
              {currentVideo.name}
            </h3>
            <span className="font-medium font-primary text-sm text-[#4c4c4c]">
              {currentVideo.description}
            </span>
            <div className="flex mt-3 items-center justify-between">
              <span className="font-medium text-text-muted text-sm">
                {`${currentVideo.published_at} | ${currentVideo.webinar_time}`}
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
